import type { SaveStatus } from '../../hooks/useAutoSaveToCatalog';

interface SaveStatusIndicatorProps {
  status: SaveStatus;
  onRetry?: () => void;
}

/**
 * Visual indicator showing the auto-save status.
 *
 * - idle: Hidden (character unnamed or no changes)
 * - saving: Spinner with "Saving..."
 * - saved: Checkmark with "Saved"
 * - error: Warning icon with "Save failed" + retry option
 */
export function SaveStatusIndicator({
  status,
  onRetry,
}: SaveStatusIndicatorProps) {
  if (status === 'idle') {
    return null;
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      {status === 'saving' && (
        <>
          <svg
            className="h-4 w-4 animate-spin text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-slate-400">Saving...</span>
        </>
      )}

      {status === 'saved' && (
        <>
          <svg
            className="h-4 w-4 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span className="text-green-500">Saved</span>
        </>
      )}

      {status === 'error' && (
        <>
          <svg
            className="h-4 w-4 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-red-500">Save failed</span>
          {onRetry && (
            <button
              onClick={onRetry}
              className="ml-1 text-red-400 underline hover:text-red-300"
            >
              Retry
            </button>
          )}
        </>
      )}
    </div>
  );
}
